import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Cat, Check, Loader2, ScanSearch, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { BREEDS, CAT_AVATARS } from '../../mock';
import BreedIdentifier from '../../components/BreedIdentifier';
import api from '../../api';

const AVATAR_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/avatars/`;

export default function EditCat() {
  const nav = useNavigate();
  const { id } = useParams();
  const { cats, updateCat } = useApp();
  const existing = cats.find((c) => c.id === id);

  const [name, setName] = useState(existing?.name || '');
  const [breed, setBreed] = useState(existing?.breed || BREEDS[0]);
  const [age, setAge] = useState(existing?.age || '');
  const [weight, setWeight] = useState(existing?.weight || '');
  const [weightUnit, setWeightUnit] = useState(existing?.weight_unit || 'lbs');
  const [avatar, setAvatar] = useState(existing?.avatar || CAT_AVATARS[0]);
  const [busy, setBusy] = useState(false);
  const [showIdentifier, setShowIdentifier] = useState(false);
  const fileRef = useRef(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const preset = CAT_AVATARS.includes(avatar);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const r = await api.post('/cats/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAvatar(AVATAR_BASE + r.data.path);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not upload photo'); }
    finally { setUploadBusy(false); }
  };

  const pickBreed = (name) => {
    setBreed(name);
    setShowIdentifier(false);
    toast.success(`Breed set to ${name}`);
  };

  if (!existing) {
    return (
      <div className="min-h-screen bg-background px-5 py-10 text-center">
        <p className="text-muted-foreground">Cat not found.</p>
        <button onClick={() => nav('/app/profile')} className="coral-btn mt-4 rounded-2xl px-5 py-2.5 text-sm font-bold text-white">Back to Profile</button>
      </div>
    );
  }

  const save = async () => {
    if (!name.trim()) { toast.error("Please add your cat's name"); return; }
    setBusy(true);
    try {
      await updateCat(id, { name: name.trim(), breed, age, avatar, weight, weight_unit: weightUnit });
      toast.success('Profile updated');
      nav('/app/profile');
    } catch (e) { toast.error('Could not save changes'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-6">
      <button onClick={() => nav('/app/profile')} className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary" data-testid="edit-cat-back">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <div className="mx-auto max-w-md animate-fade-up">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent"><Cat className="h-7 w-7 text-[hsl(var(--coral))]" /></div>
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight">Edit {existing.name}.</h1>
        <p className="mt-2 text-muted-foreground">Keep your cat's details up to date.</p>

        <div className="mt-6">
          <Label className="text-sm font-semibold">Choose a photo</Label>
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="edit-cat-upload-input" />
          <div className="mt-2 flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {!preset && avatar && (
              <button className="relative shrink-0">
                <img src={avatar} alt="your cat" className="h-16 w-16 rounded-2xl object-cover ring-4 ring-[hsl(var(--coral))]" />
                <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--coral))]"><Check className="h-3 w-3 text-white" /></span>
              </button>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={uploadBusy} data-testid="edit-cat-upload-btn" className="flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-border bg-secondary/40 text-muted-foreground hover:border-[hsl(var(--coral))]">
              {uploadBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-4 w-4" /><span className="text-[10px] font-bold">Upload</span></>}
            </button>
            {CAT_AVATARS.map((a) => (
              <button key={a} onClick={() => setAvatar(a)} className="relative shrink-0">
                <img src={a} alt="cat" className={`h-16 w-16 rounded-2xl object-cover transition ${avatar === a ? 'ring-4 ring-[hsl(var(--coral))]' : 'ring-1 ring-border opacity-80'}`} />
                {avatar === a && <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--coral))]"><Check className="h-3 w-3 text-white" /></span>}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <Label className="text-sm font-semibold">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-cat-name" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Breed</Label>
              <button type="button" onClick={() => setShowIdentifier((v) => !v)} data-testid="edit-cat-identify-toggle" className="inline-flex items-center gap-1 text-xs font-bold text-[hsl(var(--coral))]">
                <ScanSearch className="h-3.5 w-3.5" />Not sure? Identify from a photo
              </button>
            </div>
            <select value={breed} onChange={(e) => setBreed(e.target.value)} data-testid="edit-cat-breed" className="mt-1.5 h-12 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
              {!BREEDS.includes(breed) && breed && <option value={breed}>{breed}</option>}
              {BREEDS.map((b) => <option key={b}>{b}</option>)}
            </select>
            {showIdentifier && (
              <div className="mt-3 rounded-2xl border border-dashed border-border bg-secondary/30 p-3" data-testid="edit-cat-identifier">
                <p className="mb-2 text-xs text-muted-foreground">Especially handy for mixed-breed cats — tap "Use this breed" on a result to fill the field.</p>
                <BreedIdentifier onPick={pickBreed} />
              </div>
            )}
          </div>
          <div>
            <Label className="text-sm font-semibold">Age (years)</Label>
            <Input value={age} onChange={(e) => setAge(e.target.value)} data-testid="edit-cat-age" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Weight <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <div className="mt-1.5 flex gap-2">
              <Input value={weight} onChange={(e) => setWeight(e.target.value)} inputMode="decimal" data-testid="edit-cat-weight" className="h-12 flex-1 rounded-xl bg-white" />
              <select value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)} data-testid="edit-cat-weight-unit" className="h-12 w-24 rounded-xl border border-input bg-white px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                <option value="lbs">lbs</option>
                <option value="kg">kg</option>
              </select>
            </div>
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="edit-cat-save" className="coral-btn mt-8 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
          {busy && <Loader2 className="h-5 w-5 animate-spin" />}Save changes
        </button>
      </div>
    </div>
  );
}
